import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, X, Save, Loader2 } from "lucide-react";
import { admin } from "@/lib/api";
import { Field, TabHeader, Badge, inputCls, textareaCls } from "@/admin/ui";

const GROUPS = [
  ["home", "Homepage section"],
  ["general", "FAQ page — General"],
  ["services", "FAQ page — Services & Approach"],
  ["booking", "FAQ page — Booking & Logistics"],
];

const EMPTY = { q: "", a: "", group: "home", order: 99 };

export default function FaqsTab() {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => admin.list("faqs").then(setItems).catch(() => toast.error("Could not load FAQs"));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const save = async () => {
    if (!form.q.trim() || !form.a.trim()) return toast.error("Question and answer are required");
    setSaving(true);
    try {
      const payload = { ...form, order: Number(form.order) || 99 };
      if (form.id) await admin.update("faqs", form.id, payload);
      else await admin.create("faqs", payload);
      toast.success("FAQ saved");
      setForm(null);
      load();
    } catch {
      toast.error("Save failed — please try again");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (f) => {
    if (!window.confirm(`Delete "${f.q}"?`)) return;
    await admin.remove("faqs", f.id);
    toast.success("FAQ deleted");
    load();
  };

  if (form) {
    return (
      <div data-testid="admin-faq-form">
        <div className="flex items-center justify-between">
          <h1 className="font-heading text-2xl font-semibold text-foreground">{form.id ? "Edit FAQ" : "New FAQ"}</h1>
          <button onClick={() => setForm(null)} className="btn-secondary !px-4 !py-2 !text-xs"><X className="h-3.5 w-3.5" /> Cancel</button>
        </div>
        <div className="mt-8 grid gap-5 rounded-3xl border border-border bg-card p-7 shadow-soft">
          <Field label="Question"><input className={inputCls} value={form.q} onChange={set("q")} data-testid="faq-question-input" /></Field>
          <Field label="Answer"><textarea className={textareaCls} rows={4} value={form.a} onChange={set("a")} data-testid="faq-answer-input" /></Field>
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Where it appears">
              <select className={inputCls} value={form.group} onChange={set("group")} data-testid="faq-group-select">
                {GROUPS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
            <Field label="Order" hint="Lower numbers appear first.">
              <input type="number" className={inputCls} value={form.order} onChange={set("order")} data-testid="faq-order-input" />
            </Field>
          </div>
        </div>
        <button onClick={save} disabled={saving} data-testid="admin-faq-save" className="btn-primary mt-6 disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {saving ? "Saving…" : "Save FAQ"}
        </button>
      </div>
    );
  }

  return (
    <div data-testid="admin-faqs-tab">
      <TabHeader
        title="FAQs"
        description="Update the questions on your homepage and FAQ page."
        actionLabel="New FAQ"
        onAdd={() => setForm({ ...EMPTY })}
        testId="admin-add-faq"
      />
      <div className="mt-8 space-y-3">
        {(items || []).map((f) => (
          <div key={f.id} data-testid={`admin-faq-row-${f.id}`} className="flex flex-wrap items-center gap-3 rounded-2xl border border-border bg-card px-5 py-4 shadow-soft">
            <p className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">{f.q}</p>
            <Badge tone={f.group === "home" ? "terracotta" : "forest"}>
              {GROUPS.find(([v]) => v === f.group)?.[1] || f.group}
            </Badge>
            <button onClick={() => setForm({ ...f })} data-testid={`admin-faq-edit-${f.id}`} aria-label="Edit FAQ" className="rounded-full border border-border p-2 text-muted-foreground transition-colors duration-300 hover:border-terracotta-500 hover:text-terracotta-500">
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button onClick={() => remove(f)} data-testid={`admin-faq-delete-${f.id}`} aria-label="Delete FAQ" className="rounded-full border border-border p-2 text-muted-foreground transition-colors duration-300 hover:border-destructive hover:text-destructive">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
